import { isFulfilled } from "@reduxjs/toolkit";
import { typeOrder } from "constants/AppConstants";
import { useLoader } from "contexts/LoaderContext";
import { useFormTemplate, useGroupedItems } from "hooks/commonHandlers";
import { useAgentFormSync } from "hooks/useAgentFormSync";
import { useDeviceType } from "hooks/useDeviceType";
import SupportDocument from "pages/Agent/SupportDocument";
import LandlordLayout from "pages/Layout/Landlord/LandlordLayout";
import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { LANDLORD_APPLICATION_COMPLETED } from "routes/landlordRoutes";
import LandlordApplicationSuccess from "./LandlordApplicationSuccess";
import { formatTenantList, getAllErrorMessages } from "services/TenantSubmitHandler";
import { useAppDispatch, useAppSelector } from "store/configureStore";
import { fetchFormTemplates } from "store/formTemplate/formTemplateThunks";
import { FormJsonSection, PetDataFormat } from "store/formTemplate/formTemplateTypes";
import { getAgentApprovedResponse, submitLandLordResponseThunk, submitTenantResponseThunk } from "store/tenant/thunks";
import { AgentApprovedPets, SubmitAgentRequestPayload, TenantPetsData, TenantPetsDocument } from "store/tenant/types";
import { FormTemplateItem, hasFieldsList } from "types/formTemplate";
import { getProfileTitle, isQldRegion } from "utils/petFormHelpers";
import { getAnimalCardProfile, renderTemplates } from "utils/renderTemplates";
import { IconName } from "../../components/AnimalCard";
import AnimalProfileModal from "../../components/AnimalProfile/AnimalProfileModal";
import ApplicationSummary from "../../components/ApplicationSummary";
import Button from "../../components/UI/Button";
import ErrorMessages from "../../components/UI/ErrorMessages";
import Modal from "../../components/UI/Modal";
import LandlordViewData from "./LandlordViewData";

type DynamicItem = {
  type: IconName;
  uid: number;
  pet_id?: string | null;
};

type FormValues = {
  username: string;
  role: string;
  [key: string]: any; // Allow dynamic keys for animal types
};

const LandLord: React.FC = () => {
  const modalForm = useForm<FormValues>({});
  // Redux selectors
  const dispatch = useAppDispatch();
  const agentApprovedApplicationId = useAppSelector(
    (state) => state.Tenant.agentApprovedApplicationId
  );
  const primaryTenants = useAppSelector((state) => state.Tenant.agentAprovedTenants);
  const dayLeftToAutoComplete = useAppSelector((state) => state.Tenant.dayLeftToAutoComplete);
  const tenantPropertyAddress = useAppSelector(
    (state) => state.Tenant.tenantPropertyAddress
  );
  const stageOnePetsData = useAppSelector((state) => state.Tenant.agentApprovedPets);
  const stageOnePetsDocumentData = useAppSelector(
    (state) => state.Tenant.tenantPetsDocuments
  );
  const rawFormTemplate = useAppSelector(
    (state) => state.formTemplate.templates
  );
  const applicationStatus = useAppSelector((state) => state.Tenant.landlordApplicationStatus);
  const tenantAgentId = useAppSelector((state) => state.Tenant.tenantAgentId);
  const landlordAgentId = useAppSelector((state) => state.Tenant.landlordAgentId);
  const landlordAgentLink = useAppSelector((state) => state.Tenant.landlordAgentLink);

  const {
    agentApprovedApplications,
    agentApprovedlandlord,
    agentApprovedPetsDetails,
  } = useAppSelector((state) => ({
    agentApprovedResponse: state.Tenant.agentApprovedResponse,
    agentApprovedApplications: state.Tenant.agentApprovedApplications,
    agentApprovedlandlord: state.Tenant.agentApprovedlandlord,
    agentApprovedPetsDetails: state.Tenant.agentApprovedPetsDetails,
  }));


  // Local state
  const [nextUid, setNextUid] = useState(1);
  const [isAllApproved, setIsAllApproved] = useState(true);
  const [activeStage, setActiveStage] = useState(1);
  const [items, setItems] = useState<DynamicItem[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedPetData, setSelectedPetData] = useState<any>(null);
  const [selectedPetDocuments, setSelectedPetDocuments] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedIdx, setSelectedIdx] = useState<number>(0);
  const [formErrorMessages, setFormErrorMessages] = useState<string[]>([]);

  // Form methods
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      landlords_first_name: agentApprovedlandlord && agentApprovedlandlord?.first_name,
      landlords_last_name: agentApprovedlandlord && agentApprovedlandlord?.last_name,
      landlords_email: agentApprovedlandlord && agentApprovedlandlord?.email,
      landlords_phone: agentApprovedlandlord && agentApprovedlandlord?.phone,
      // ...add other fields as needed
    }
  });
  const { register, handleSubmit, getValues, watch, setValue, reset, unregister, formState, trigger } = methods;
  const { isValid } = formState;


  // Routing variables - store initial landlordId in state to prevent URL changes
  // (e.g. navigation to /application-approved) from corrupting the ID
  // Set once the decision is saved so the success page shows even if the route change lags
  const [submitted, setSubmitted] = useState(false);
  const [landLordId] = useState(() => {
    const urlParts = window.location.pathname.split("/");
    return urlParts?.[2];
  });

  // Loader context
  const { showLoader, hideLoader } = useLoader();

  const deviceType = useDeviceType();

  // Hooks
  const formTemplate = useFormTemplate(rawFormTemplate);
  const groupedItems = useGroupedItems(items);
  const agentFomTemplate = useFormTemplate(rawFormTemplate);

  const navigate = useNavigate();


  // Effects
  useEffect(() => {
    showLoader();
    dispatch(getAgentApprovedResponse({ landLordId }))
      .finally(hideLoader);
  }, [landLordId, dispatch, showLoader, hideLoader]);

  useEffect(() => {
    if (tenantAgentId) {
      showLoader();
      const region = localStorage.getItem('pet_app_region') || undefined;
      dispatch(fetchFormTemplates({ agentId: tenantAgentId, region }))
        .finally(hideLoader);
    }
  }, [tenantAgentId, dispatch, showLoader, hideLoader]);



    // Update items state using agentApprovedPetsDetails
    useEffect(() => {
      if (agentApprovedPetsDetails && agentApprovedPetsDetails.length > 0) {
        let uidCounter = 0;
        let lastType: string | null = null;
        const approvedItems = typeOrder.flatMap((type) =>
          agentApprovedPetsDetails
            .filter((pet: AgentApprovedPets) => pet.pet_type === type)
            .map((pet: AgentApprovedPets) => {
              if (lastType !== type) {
                uidCounter = 0;
                lastType = type;
              }
              const item = {
                type: pet.pet_type as IconName,
                uid: uidCounter,
                pet_id: pet.id,
              };
              uidCounter++;
              return item;
            })
        );
        setItems(approvedItems);
        setNextUid(approvedItems.length + 1);
        setIsAllApproved(
          agentApprovedPetsDetails &&
          agentApprovedPetsDetails[0] &&
          Array.isArray(agentApprovedPetsDetails[0].agent_comments) &&
          agentApprovedPetsDetails[0].agent_comments.length > 0
        ); // reset to all approved
      }
    }, [agentApprovedPetsDetails]);

    /**
   * Added to reset conditions if "I do not consent" is selected, faced below issue while testing
    If the user first selects "I consent" and "no conditions", but then changes to "I do not consent", 
    the previously selected "I consent" and "no conditions" values should be reset to null.
   */
  useAgentFormSync({
    items,
    agentApprovedlandlord,
    agentApprovedApplications,
    agentApprovedPetsDetails: agentApprovedPetsDetails ?? [],
    setValue,
    watch,
    reset,
    control: methods.control,
    trigger
  });

 

  const handleAddCard = useCallback((type: IconName) => {
    setItems(prev => [...prev, { type, uid: nextUid }]);
    setNextUid(prev => prev + 1);
  }, [nextUid]);

  const handleUploadPetDocuments = React.useCallback((uploadedFiles: any) => {},
  []);

 const renderAnimalCards = useCallback((animalType: string, animalIndex: number) => {
    const profiles = getAnimalCardProfile(formTemplate, animalType)?.[0] || [];
    if (profiles?.fieldsList?.length > 0) {
      return renderTemplates(
        profiles.fieldsList,
        { register, getValues, watch, handleAddCard, handleUploadPetDocuments },
        animalIndex,
        animalType,
        stageOnePetsData,
        stageOnePetsDocumentData,
        "agent"
      );
    }
    return null;
  }, [formTemplate, register, getValues, watch, handleAddCard, handleUploadPetDocuments, stageOnePetsData, stageOnePetsDocumentData]);


    /**
   * Handle bulk approve all pets - toggle between approve all and reset all
   * If not all approved, set all to approved, else reset all to empty
   * Also update button text accordingly
   */
  const handleBulkApproveAll = useCallback(() => {
    if(isAllApproved) {
      // If currently all approved, reset/clear all values
      items.forEach(item => {
        setValue(`${item.type}.agent_consent.${item.uid}`, "");
        setValue(`${item.type}.agent_consent_conditions.${item.uid}`, "");
        unregister(`${item.type}.agent_consent_conditions.${item.uid}`);
        unregister(`${item.type}.agent_consent.${item.uid}`);

      });
    } else {
      // If not all approved, set all to approved
      items.forEach(item => {
        setValue(`${item.type}.agent_consent.${item.uid}`, "i_consent");
        setValue(`${item.type}.agent_consent_conditions.${item.uid}`, "i_consent_no_conditions");
      });
    }
    setIsAllApproved(!isAllApproved);
    const btn = document.querySelector("#bulk-approve-button");
    if (btn) btn.innerHTML = !isAllApproved 
    ? `<svg fill=none height=24 viewBox="0 0 24 24"width=24 xmlns=http://www.w3.org/2000/svg><path d="M10.709 2.53613C11.0399 2.29975 11.5 2.53667 11.5 2.94336V5.5H12C16.1439 5.5 19.5 8.85614 19.5 13C19.5 17.1439 16.1439 20.5 12 20.5C8.1734 20.5 5.0178 17.6377 4.55762 13.9365C4.53173 13.7283 4.70487 13.5 5 13.5C5.2592 13.5 5.53026 13.7229 5.58984 14.0781C6.10479 17.1521 8.78066 19.5 12 19.5C15.5861 19.5 18.5 16.5861 18.5 13C18.5 9.41386 15.5861 6.5 12 6.5H11.5V9.05664C11.5 9.46333 11.0399 9.70025 10.709 9.46387L6.42969 6.40723C6.15051 6.20782 6.15051 5.79218 6.42969 5.59277L10.709 2.53613Z"fill=white stroke=white /></svg>Undo Bulk Approval`
      : `<svg fill=none height=24 viewBox="0 0 24 24"width=24 xmlns=http://www.w3.org/2000/svg><circle cx=12 cy=12 r=9 stroke=white stroke-width=2 /><path clip-rule=evenodd d="M16.3683 9.13327C16.5445 9.31036 16.5438 9.59679 16.3667 9.77303L10.4575 15.654C10.281 15.8296 9.99577 15.8296 9.81928 15.654L7.13327 12.9808C6.95618 12.8046 6.95549 12.5181 7.13173 12.3411C7.30798 12.164 7.59441 12.1633 7.77149 12.3395L10.1384 14.6951L15.7285 9.13173C15.9056 8.95549 16.192 8.95618 16.3683 9.13327Z"fill=white fill-rule=evenodd stroke=white stroke-linecap=round stroke-linejoin=round /></svg>  Mark All Approved`;
  }, [items, isAllApproved, setValue]);

  useEffect(() => {
   setTimeout(() => {
     const btn = document.querySelector("#bulk-approve-button");
    if (btn) btn.innerHTML = isAllApproved 
    ? `<svg fill=none height=24 viewBox="0 0 24 24"width=24 xmlns=http://www.w3.org/2000/svg><path d="M10.709 2.53613C11.0399 2.29975 11.5 2.53667 11.5 2.94336V5.5H12C16.1439 5.5 19.5 8.85614 19.5 13C19.5 17.1439 16.1439 20.5 12 20.5C8.1734 20.5 5.0178 17.6377 4.55762 13.9365C4.53173 13.7283 4.70487 13.5 5 13.5C5.2592 13.5 5.53026 13.7229 5.58984 14.0781C6.10479 17.1521 8.78066 19.5 12 19.5C15.5861 19.5 18.5 16.5861 18.5 13C18.5 9.41386 15.5861 6.5 12 6.5H11.5V9.05664C11.5 9.46333 11.0399 9.70025 10.709 9.46387L6.42969 6.40723C6.15051 6.20782 6.15051 5.79218 6.42969 5.59277L10.709 2.53613Z"fill=white stroke=white /></svg>Undo Bulk Approval`
      : `<svg fill=none height=24 viewBox="0 0 24 24"width=24 xmlns=http://www.w3.org/2000/svg><circle cx=12 cy=12 r=9 stroke=white stroke-width=2 /><path clip-rule=evenodd d="M16.3683 9.13327C16.5445 9.31036 16.5438 9.59679 16.3667 9.77303L10.4575 15.654C10.281 15.8296 9.99577 15.8296 9.81928 15.654L7.13327 12.9808C6.95618 12.8046 6.95549 12.5181 7.13173 12.3411C7.30798 12.164 7.59441 12.1633 7.77149 12.3395L10.1384 14.6951L15.7285 9.13173C15.9056 8.95549 16.192 8.95618 16.3683 9.13327Z"fill=white fill-rule=evenodd stroke=white stroke-linecap=round stroke-linejoin=round /></svg>  Mark All Approved`;
   }, 2000)
  
  }, [isAllApproved]);

  /**
   * render section based on form type
   * @param formType 
   * @param animalType 
   * @param animalIndex 
   * @returns 
   */
  const renderSection = useCallback((formType: string, animalType?: string, animalIndex?: number) => {
    const rawAgentFormTemplate = (agentFomTemplate as FormJsonSection[])?.find((template) => template.type === formType);
    if (rawAgentFormTemplate && hasFieldsList(rawAgentFormTemplate as FormTemplateItem)) {
      return renderTemplates(
        rawAgentFormTemplate.fieldsList,
        { register, getValues, watch, handleAddCard, handleUploadPetDocuments, handleBulkApproveAll },
        animalIndex,
        animalType
      );
    }
    return null;
  }, [agentFomTemplate, register, getValues, watch, handleAddCard, handleUploadPetDocuments, handleBulkApproveAll]);

  /**
   * Get pet input fields by pet UUID
   * @param formData - Form data from react-hook-form
   * @param items - List of DynamicItem with type, uid, pet_id
   * @param petUuid - UUID of the pet to find inputs for
   * @returns Object with field names and values for the specified pet, or null if not found
   */
   const getPetInputByUuid = useCallback((
    formData: { [key: string]: { [key: string]: { [key: number]: any } } },
    items: DynamicItem[],
    petUuid: string | null
   ) => {
    const item = items.find((i: DynamicItem) => i.pet_id === petUuid);
    if (!item) return null;
    const { type, uid } = item;
    const petFields: { [key: string]: any } = {};
    Object.keys(formData[type] || {}).forEach((field: string) => {
      if (formData[type][field][uid] !== undefined) petFields[field] = formData[type][field][uid];
    });
    return petFields;
  }, []);

  const submitPetInfo = async (data: any) => {
    let requestPayload = {};
    const petsPayload = items.map(item => ({
      pet_id: item.pet_id,
      pet_type: item.type,
      pet_index: item.uid,
      fields: getPetInputByUuid(data, items, item.pet_id ?? null)
    }));
    requestPayload = {
      landlords_first_name: data?.landlords_first_name,
      landlords_lastname: data?.landlords_last_name,
      landlords_email: data?.landlords_email,
      landlords_phone: data?.landlords_phone,
      general_consent: data?.general_consent,
      conditions_tenants: data?.conditions_tenants,
      pets: petsPayload,
      application_type: "send_email",
      save_type: "send_email",
      from: "landlord",
      email_notification: data?.email_notification,
      sms_notification: data?.sms_notification,
      agent_decision_type: data?.agent_decision_type,
    };

    try {
      showLoader();
      const response = await dispatch(submitTenantResponseThunk({ agentId: landlordAgentId || "", applicationId: landlordAgentLink || "", agentResponseData: requestPayload as SubmitAgentRequestPayload }));
      return response;
    } catch (error) {
      // API error occurred
    } finally{
      hideLoader();
    }

  };


  /**
   * Form submission handler
   * @param data - Form data collected from react-hook-form
   */
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      showLoader();
      const petSaveResponse = await submitPetInfo(data);
      if(isFulfilled(petSaveResponse)){
        // QLD has no landlord signing/consent - skip the second save API and navigate directly
        if (isQldRegion()) {
          setSubmitted(true);
          navigate(LANDLORD_APPLICATION_COMPLETED, { replace: true });
        } else {
          // NSW: Call landlord save API for signing consent
          showLoader()
          const agentResponse = await dispatch(submitLandLordResponseThunk({
            landLordId: landLordId || "",
            applicationId: agentApprovedApplicationId || "",
            landlord_general_consent: data?.general_consent || false,
            landlord_conditions_discussed: data?.conditions_tenants || false,
          }));
          if(isFulfilled(agentResponse)){
            setSubmitted(true);
            navigate(LANDLORD_APPLICATION_COMPLETED, { replace: true });
          }else{
            setFormErrorMessages(["Something went wrong while saving landlord information. Please try again."]);
          }
        }
      }else{
          setFormErrorMessages(["Something went wrong while saving pet information. Please try again."]);
      }

    } catch (error) {
      const errorMessages = getAllErrorMessages(error);
      setFormErrorMessages(errorMessages);
    } finally{
      hideLoader();
    }

  };

  const handleViewProfile = (
    type: string,
    idx: number,
    petData: PetDataFormat | TenantPetsData | null,
    petDocuments: TenantPetsDocument[]
  ) => {
    setSelectedPetData(petData);
    setSelectedPetDocuments(petDocuments);
    setSelectedType(type);
    setSelectedIdx(idx);
    // Reset modal form with selected pet data
    modalForm.reset(petData || {});
    setShowProfileModal(true);
  };

  // Link is single-use: once actioned, show the success page instead of the form
  const isActioned = ["landlord_approved", "landlord_declined", "completed", "auto_approved"].includes(applicationStatus || "");

  useEffect(() => {
    if (isActioned) {
      navigate(LANDLORD_APPLICATION_COMPLETED, { replace: true });
    }
  }, [isActioned, navigate]);

  // Render the success page directly so the form never shows, even if the route change lags
  if (isActioned || submitted) {
    return <LandlordApplicationSuccess />;
  }




  // Render starts here
  return (
    <>
    {/* {error && <ErrorAlert message={error} />}
    {
      !error && ( */}
      <LandlordLayout roleType="agent">
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              // Prevent Enter key submission except in textarea elements
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}
          >
            <div className="d-flex flex-column gap-40px">
              <div className="w-100 max-w-960px px-30px px-lg-0 mx-auto d-flex flex-column gap-40px">


                {/* Application summary section as reusable component */}
                <ApplicationSummary
                  applicationReceivedOn={agentApprovedApplications && agentApprovedApplications?.agent_reviewed_at}
                  primaryTenants={primaryTenants ?? []}
                  tenantPropertyAddress={tenantPropertyAddress}
                  formatTenantList={formatTenantList}
                  daysLeft={dayLeftToAutoComplete}
                  approvalText="automatic approval without conditions"
                />

                {activeStage === 1 && (
                  <LandlordViewData
                    stageOnePetsData={stageOnePetsData ?? []}
                    stageOnePetsDocumentData={stageOnePetsDocumentData ?? []}
                    activeStage={activeStage}
                    items={items}
                    agentFomTemplate={agentFomTemplate}
                    groupedItems={groupedItems}
                    handleAddCard={handleAddCard}
                    handleUploadPetDocuments={handleUploadPetDocuments}
                    renderSection={renderSection}
                    renderTemplates={renderTemplates}
                    register={register}
                    handleBulkApproveAll={handleBulkApproveAll}
                    handleViewProfile={handleViewProfile}
                  />
                )}

                
                <ErrorMessages apiResponse={formErrorMessages} />

                <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-end position-xs-fixed bottom-0 start-0 end-0 bg-white p-30px p-md-0 drop-shadow-xs-2">
                  {activeStage === 1 && (
                    <Button
                      iconName={"check"}
                      iconPosition={"left"}
                      text={isQldRegion() ? "Submit" : "Proceed To Signing"}
                      type="submit"
                      disabled={!isValid}                      
                    />
                  )}
                  
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </LandlordLayout>
    {/* //   )
    // } */}

    {showProfileModal && selectedPetData && (
      <Modal width="960" onClose={() => setShowProfileModal(false)}>
        <FormProvider {...modalForm}>
          <Button
              className="modal_close_btn d-block d-md-none position-absolute text-white text-md-black top-43px right-30px"
              iconName="cross"
              iconPosition="left"
              text="Close"
              onClick={() => setShowProfileModal(false)}
              aria-label="Close"
              type="button"
            />
          <AnimalProfileModal
            type={selectedType as IconName}
            title={getProfileTitle(selectedType, selectedIdx)}
            animalType={selectedType as IconName}
            petData={selectedPetData ? [selectedPetData] : undefined}
            petDocumentData={selectedPetDocuments}
            roleType="agent"
            activeState={1}
            keyIndex={selectedIdx}
            onRemove={() => {}}
            isModal={true}
            maxHight={deviceType === "mobile" ? true : false}          
          >
            {/* Only one close button for modal */}
            
            <Button
              className="modal_close_btn d-none d-md-block position-absolute top-43px right-30px"
              iconName="cross"
              iconPosition="left"
              text="Close"
              onClick={() => setShowProfileModal(false)}
              aria-label="Close"
            />
            {/* Render only one set of fields for selected profile */}
            {formTemplate && formTemplate.length > 0 && renderAnimalCards(selectedType, selectedIdx)}
            {selectedPetDocuments &&
              selectedPetDocuments.length > 0 &&
              <>
                <div className="fs-16px fw-normal text-black">
                    <strong>{isQldRegion() ? "9" : selectedType === "fish" ? "3" : "4"}. Supporting Documents</strong>      
                </div>
                {selectedPetDocuments.map((document, idKey) => (
                  <SupportDocument key={idKey} item={document} />
                ))}
              </>
            }
          </AnimalProfileModal>
        </FormProvider>
      </Modal>
    )}
    </>
  );
};

export default LandLord;
